import React, { useState, useEffect, useRef } from 'react';
import { DailyWeather } from '../models/WeatherData';
import Chart from 'chart.js/auto';
import { useLanguage } from '../contexts/LanguageContext';
import { useSettings } from '../contexts/SettingsContext';
import { 
  convertTemperature, 
  convertPressure, 
  convertWindSpeed,
  convertPrecipitation,
  getTemperatureUnit,
  getPressureUnit,
  getWindSpeedUnit,
  getPrecipitationUnit
} from '../utils/unitConversion';

interface WeatherHistoryProps {
  dailyWeather: DailyWeather[];
  selectedDate?: string;
}

type ChartTab = 'temperature' | 'pressure' | 'humidity' | 'wind' | 'precipitation' | 'astronomy';

const WeatherHistory: React.FC<WeatherHistoryProps> = ({ dailyWeather, selectedDate }) => {
  const { t } = useLanguage();
  const { unitSystem } = useSettings();
  const [activeTab, setActiveTab] = useState<ChartTab>('temperature');
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstanceRef = useRef<Chart | null>(null);

  useEffect(() => {
    if (!chartRef.current || !dailyWeather || dailyWeather.length === 0) return;

    // Destroy previous chart
    if (chartInstanceRef.current) {
      chartInstanceRef.current.destroy();
    }

    const ctx = chartRef.current.getContext('2d');
    if (!ctx) return;

    const labels = dailyWeather.map(day => {
      const date = new Date(day.date);
      return date.toLocaleDateString('sv-SE', { month: 'short', day: 'numeric' });
    });

    let datasets: any[] = [];
    let yAxisConfig: any = {};
    let scales: any = {};

    switch (activeTab) {
      case 'temperature':
        datasets = [
          {
            label: `${t.maxTemp} (${getTemperatureUnit(unitSystem)})`,
            data: dailyWeather.map(d => convertTemperature(d.tempMax || d.temperature, unitSystem)),
            borderColor: 'rgba(239, 68, 68, 0.8)',
            backgroundColor: 'rgba(239, 68, 68, 0.1)',
            tension: 0.4,
            fill: false
          },
          {
            label: `${t.minTemp} (${getTemperatureUnit(unitSystem)})`,
            data: dailyWeather.map(d => convertTemperature(d.tempMin || d.temperature, unitSystem)),
            borderColor: 'rgba(59, 130, 246, 0.8)',
            backgroundColor: 'rgba(59, 130, 246, 0.1)',
            tension: 0.4,
            fill: false
          }
        ];
        yAxisConfig = {
          title: { 
            display: true, 
            text: `${t.temperature} (${getTemperatureUnit(unitSystem)})`, 
            color: 'rgba(255, 255, 255, 0.7)',
            font: { size: 12 }
          }
        };
        break;

      case 'pressure':
        datasets = [
          {
            label: `${t.pressure} (${getPressureUnit(unitSystem)})`,
            data: dailyWeather.map(d => convertPressure(d.pressure, unitSystem)),
            borderColor: 'rgba(59, 130, 246, 0.8)',
            backgroundColor: 'rgba(59, 130, 246, 0.1)',
            tension: 0.4,
            fill: true
          }
        ];
        yAxisConfig = {
          title: { 
            display: true, 
            text: `${t.pressure} (${getPressureUnit(unitSystem)})`, 
            color: 'rgba(255, 255, 255, 0.7)',
            font: { size: 12 }
          }
        };
        break;

      case 'humidity':
        datasets = [
          {
            label: t.humidity,
            data: dailyWeather.map(d => d.humidity),
            borderColor: 'rgba(34, 197, 94, 0.8)',
            backgroundColor: 'rgba(34, 197, 94, 0.1)',
            tension: 0.4,
            fill: true
          }
        ];
        yAxisConfig = {
          title: { 
            display: true, 
            text: `${t.humidity} (%)`, 
            color: 'rgba(255, 255, 255, 0.7)',
            font: { size: 12 }
          }
        };
        break;

      case 'wind':
        datasets = [
          {
            label: `${t.windSpeed} (${getWindSpeedUnit(unitSystem)})`,
            data: dailyWeather.map(d => convertWindSpeed(d.windSpeed, unitSystem)),
            borderColor: 'rgba(168, 85, 247, 0.8)',
            backgroundColor: 'rgba(168, 85, 247, 0.1)',
            tension: 0.4,
            fill: false
          },
          {
            label: `${t.windGustLabel} (${getWindSpeedUnit(unitSystem)})`,
            data: dailyWeather.map(d => convertWindSpeed(d.windGust || d.windSpeed, unitSystem)),
            borderColor: 'rgba(236, 72, 153, 0.8)',
            backgroundColor: 'rgba(236, 72, 153, 0.1)',
            tension: 0.4,
            fill: false,
            borderDash: [5, 5]
          },
          {
            label: `${t.windDirection} (°)`,
            data: dailyWeather.map(d => d.windDirection || 0),
            borderColor: 'rgba(251, 191, 36, 0.8)',
            backgroundColor: 'rgba(251, 191, 36, 0.1)',
            tension: 0.4,
            fill: false,
            yAxisID: 'y1',
            pointStyle: 'triangle',
            pointRadius: 4
          }
        ];
        yAxisConfig = {
          title: { 
            display: true, 
            text: `${t.windSpeed} (${getWindSpeedUnit(unitSystem)})`, 
            color: 'rgba(255, 255, 255, 0.7)',
            font: { size: 12 }
          }
        };
        scales = {
          y1: {
            type: 'linear',
            display: true,
            position: 'right',
            min: 0,
            max: 360,
            ticks: {
              color: 'rgba(251, 191, 36, 0.8)',
              font: { size: 11 },
              stepSize: 45,
              callback: function(value: number) {
                const directions: { [key: number]: string } = {
                  0: 'N', 45: 'NO', 90: 'O', 135: 'SO',
                  180: 'S', 225: 'SV', 270: 'V', 315: 'NV', 360: 'N'
                };
                return directions[value] || value + '°';
              }
            },
            grid: {
              drawOnChartArea: false,
            },
            title: {
              display: true,
              text: t.windDirection,
              color: 'rgba(251, 191, 36, 0.8)',
              font: { size: 12 }
            }
          }
        };
        break;

      case 'precipitation':
        datasets = [
          {
            label: `${t.precipitation} (${getPrecipitationUnit(unitSystem)})`,
            data: dailyWeather.map(d => convertPrecipitation(d.precipitation || 0, unitSystem)),
            backgroundColor: 'rgba(59, 130, 246, 0.6)',
            borderColor: 'rgba(59, 130, 246, 0.8)',
            borderWidth: 1,
            borderRadius: 4
          }
        ];
        yAxisConfig = {
          title: { 
            display: true, 
            text: `${t.precipitation} (${getPrecipitationUnit(unitSystem)})`, 
            color: 'rgba(255, 255, 255, 0.7)',
            font: { size: 12 }
          },
          beginAtZero: true
        };
        break;

      case 'astronomy':
        // Convert sunrise/sunset to hours for display
        const sunriseHours = dailyWeather.map(d => {
          if (!d.sunrise) return 6;
          const date = new Date(d.sunrise);
          return date.getHours() + date.getMinutes() / 60;
        });
        const sunsetHours = dailyWeather.map(d => {
          if (!d.sunset) return 18;
          const date = new Date(d.sunset);
          return date.getHours() + date.getMinutes() / 60;
        });
        const moonPhases = dailyWeather.map(d => (d.moonPhase || 0) * 100);

        datasets = [
          {
            label: t.sunrise,
            data: sunriseHours,
            borderColor: 'rgba(251, 191, 36, 0.8)',
            backgroundColor: 'rgba(251, 191, 36, 0.1)',
            tension: 0.4,
            fill: false,
            pointStyle: 'circle',
            pointRadius: 5
          },
          {
            label: t.sunset,
            data: sunsetHours,
            borderColor: 'rgba(251, 146, 60, 0.8)',
            backgroundColor: 'rgba(251, 146, 60, 0.1)',
            tension: 0.4,
            fill: false,
            pointStyle: 'circle',
            pointRadius: 5
          },
          {
            label: t.moonPhasePercent,
            data: moonPhases,
            borderColor: 'rgba(148, 163, 184, 0.8)',
            backgroundColor: 'rgba(148, 163, 184, 0.1)',
            tension: 0.4,
            fill: true,
            yAxisID: 'y1',
            pointStyle: 'rectRot',
            pointRadius: 4
          }
        ];
        yAxisConfig = {
          title: { 
            display: true, 
            text: t.timeHours, 
            color: 'rgba(255, 255, 255, 0.7)',
            font: { size: 12 }
          }
        };
        scales = {
          y1: {
            type: 'linear',
            display: true,
            position: 'right',
            min: 0,
            max: 100,
            ticks: {
              color: 'rgba(148, 163, 184, 0.8)',
              font: { size: 11 },
              callback: function(value: number) {
                return value + '%';
              }
            },
            grid: {
              drawOnChartArea: false,
            },
            title: {
              display: true,
              text: t.moonPhasePercent,
              color: 'rgba(148, 163, 184, 0.8)',
              font: { size: 12 }
            }
          }
        };
        break;
    }

    chartInstanceRef.current = new Chart(ctx, {
      type: activeTab === 'precipitation' ? 'bar' : 'line',
      data: {
        labels,
        datasets
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            labels: {
              color: 'rgba(255, 255, 255, 0.8)',
              font: { size: 12 },
              usePointStyle: false,
              boxWidth: 40,
              boxHeight: 3,
              padding: 10
            }
          },
          tooltip: {
            backgroundColor: 'rgba(15, 23, 42, 0.9)',
            titleColor: '#fff',
            bodyColor: '#cbd5e1',
            borderColor: 'rgba(255, 255, 255, 0.1)',
            borderWidth: 1,
            padding: 12,
            displayColors: true,
            callbacks: {
              afterLabel: function(context: any) {
                // Show compass direction in tooltip for wind data
                if (activeTab === 'wind' && context.dataset.label?.includes('(°)')) {
                  const degrees = context.parsed.y;
                  const directions = [t.north, t.northeast, t.east, t.southeast, t.south, t.southwest, t.west, t.northwest];
                  const index = Math.round(((degrees % 360) / 45)) % 8;
                  const direction = directions[index];
                  return `${direction} (${degrees}°)`;
                }
                return '';
              }
            }
          }
        },
        scales: {
          x: {
            grid: {
              color: 'rgba(255, 255, 255, 0.1)'
            },
            ticks: {
              color: 'rgba(255, 255, 255, 0.6)',
              font: { size: 11 }
            }
          },
          y: {
            ...yAxisConfig,
            grid: {
              color: 'rgba(255, 255, 255, 0.1)'
            },
            ticks: {
              color: 'rgba(255, 255, 255, 0.6)',
              font: { size: 11 }
            }
          },
          ...scales
        }
      }
    });

    return () => {
      if (chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
      }
    };
  }, [dailyWeather, activeTab, t, unitSystem]);

  if (!dailyWeather || dailyWeather.length === 0) {
    return null;
  }

  const tabs: { id: ChartTab; label: string; icon: string }[] = [
    { id: 'temperature', label: t.temperature, icon: '🌡️' },
    { id: 'pressure', label: t.pressure, icon: '🔽' },
    { id: 'humidity', label: t.humidity, icon: '💧' },
    { id: 'wind', label: t.wind, icon: '💨' },
    { id: 'precipitation', label: t.precipitation, icon: '🌧️' },
    { id: 'astronomy', label: t.astronomy, icon: '🌙' }
  ];

  // Generate title with selected date
  const getHistoryTitle = () => {
    if (!selectedDate) return `📈 ${t.daysBackAndForward} ${t.today}`;
    
    const dateObj = new Date(selectedDate);
    const today = new Date().toISOString().split('T')[0];
    const isToday = selectedDate === today;
    
    if (isToday) {
      return `📈 ${t.daysBackAndForward} ${t.today}`;
    }
    
    const dateText = dateObj.toLocaleDateString('sv-SE', { 
      day: 'numeric', 
      month: 'long',
      year: 'numeric'
    });
    
    return `📈 ${t.daysBackAndForward} ${dateText}`;
  };

  return (
    <div id="weather-history">
      <div className="rounded-2xl sm:rounded-3xl bg-white/[0.02] backdrop-blur-xl border border-white/[0.05] p-4 sm:p-6">
        <div className="mb-6">
          <h3 id="weather-history-title" className="text-base sm:text-lg font-semibold text-white flex items-center gap-2 mb-2">
            {getHistoryTitle()}
          </h3>
          <p className="text-xs text-slate-400">{t.historicalWeatherDevelopment}</p>
        </div>
        
        {/* Chart tabs */}
        <div className="flex gap-2 mb-4 overflow-x-auto scrollbar-thin">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`chart-tab-btn px-3 py-2 rounded-lg text-xs font-medium transition-all whitespace-nowrap ${
                activeTab === tab.id ? 'active' : ''
              }`}
            >
              {tab.icon} {tab.label}
            </button>
          ))}
        </div>
        
        {/* Chart container */}
        <div className="bg-white/[0.02] rounded-xl p-4 h-64 sm:h-80">
          <canvas ref={chartRef} className="w-full h-full"></canvas>
        </div>
      </div>
    </div>
  );
};

export default WeatherHistory;
